import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Printer, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';

const VALORI = [
  { code: '', label: '-', cls: 'bg-white text-slate-300' },
  { code: 'P', label: 'P', cls: 'bg-brand-soft text-brand-dark font-bold' },
  { code: 'D', label: 'D', cls: 'bg-amber-100 text-amber-700 font-bold' },
  { code: 'S', label: 'S', cls: 'bg-red-100 text-red-700 font-bold' },
  { code: 'C', label: 'C', cls: 'bg-blue-100 text-blue-700 font-bold' },
];

export default function AlvoTab({ area, setArea }) {
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));
  const [dati, setDati] = useState(null);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDati = useCallback(async () => {
    setLoading(true);
    try {
      const [g, a] = await Promise.all([
        api.get('/moduli/alvo', { params: { mese, area } }),
        api.get('/moduli/alvo/alert', { params: { giorni: 3, area } }),
      ]);
      setDati(g.data);
      setAlert(a.data);
    } catch { toast.error('Errore caricamento registro alvo'); }
    setLoading(false);
  }, [mese, area]);

  useEffect(() => { fetchDati(); }, [fetchDati]);

  const ciclo = async (ospiteId, giorno, attuale) => {
    const idx = VALORI.findIndex(v => v.code === (attuale || ''));
    const nuovo = VALORI[(idx + 1) % VALORI.length].code;
    setDati(d => ({
      ...d,
      righe: d.righe.map(r => r.id === ospiteId ? { ...r, valori: { ...r.valori, [giorno]: nuovo } } : r)
    }));
    try { await api.put('/moduli/alvo', { ospite_id: ospiteId, data: giorno, valore: nuovo }); }
    catch { toast.error('Errore salvataggio'); fetchDati(); }
  };

  const stampa = () => {
    if (!dati) return;
    let html = `<table><thead><tr><th>Ospite</th><th>St.</th>${dati.giorni.map(g => `<th style="text-align:center">${g.slice(8)}</th>`).join('')}</tr></thead><tbody>`;
    dati.righe.forEach(r => {
      html += `<tr><td>${r.cognome} ${r.nome}</td><td>${r.stanza || ''}</td>${dati.giorni.map(g => `<td style="text-align:center">${r.valori[g] || ''}</td>`).join('')}</tr>`;
    });
    html += `</tbody></table><p style="font-size:9px;margin-top:6px">Legenda: P = evacuazione presente · D = diarrea · S = stipsi/assente · C = clistere</p>`;
    openPrintWindow('Registro Alvo', html, { subtitle: `Mese ${mese} · Area ${area || 'tutte'}` });
  };

  if (loading && !dati) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-4" data-testid="alvo-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={mese} onChange={e => setMese(e.target.value)} className="w-40 h-9" data-testid="alvo-mese" />
        <Select value={area || 'tutte'} onValueChange={v => setArea(v === 'tutte' ? '' : v)}>
          <SelectTrigger className="w-40 h-9" data-testid="alvo-area"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutte">Tutte le aree</SelectItem>
            <SelectItem value="verde">Area Verde</SelectItem>
            <SelectItem value="gialla">Area Gialla</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={stampa} className="h-9" data-testid="alvo-print">
          <Printer className="w-4 h-4 mr-1" />Stampa
        </Button>
        <div className="flex items-center gap-2 ml-auto text-[11px] text-slate-500">
          {VALORI.slice(1).map(v => (
            <span key={v.code} className="flex items-center gap-1">
              <span className={`w-5 h-5 rounded flex items-center justify-center ${v.cls}`}>{v.label}</span>
              {dati?.legenda?.[v.code]}
            </span>
          ))}
        </div>
      </div>

      {alert?.totale > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="alvo-alert">
          <CardContent className="p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-800">
              <strong>{alert.totale} ospiti</strong> senza evacuazione registrata da almeno 3 giorni:{' '}
              {alert.ospiti.slice(0, 12).map(o => `${o.cognome} ${o.nome}${o.giorni_senza ? ` (${o.giorni_senza}gg)` : ''}`).join(' · ')}
              {alert.ospiti.length > 12 ? ` e altri ${alert.ospiti.length - 12}` : ''}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-auto max-h-[65vh]">
          <table className="w-full text-[11px] border-collapse">
            <thead className="sticky top-0 bg-white z-10">
              <tr>
                <th className="text-left px-2 py-2 border-b border-slate-200 sticky left-0 bg-white min-w-[150px]">Ospite</th>
                <th className="px-1 py-2 border-b border-slate-200">St.</th>
                {dati.giorni.map(g => (
                  <th key={g} className="px-0 py-2 border-b border-slate-200 text-center w-7 text-slate-400">{g.slice(8)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dati.righe.map(r => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-2 py-1 border-b border-slate-100 sticky left-0 bg-white font-medium text-slate-700 whitespace-nowrap">
                    {r.cognome} {r.nome}
                  </td>
                  <td className="px-1 py-1 border-b border-slate-100 text-center text-slate-400">{r.stanza}</td>
                  {dati.giorni.map(g => {
                    const v = VALORI.find(x => x.code === (r.valori[g] || '')) || VALORI[0];
                    return (
                      <td key={g} className="border-b border-slate-100 p-0.5">
                        <button onClick={() => ciclo(r.id, g, r.valori[g])}
                          className={`w-7 h-7 rounded text-[11px] border border-slate-200 transition-colors hover:ring-1 hover:ring-brand ${v.cls}`}
                          data-testid={`alvo-cell-${r.id}-${g}`}>{v.label === '-' ? '' : v.label}</button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <p className="text-[11px] text-slate-400">Tocca una cella per cambiare valore: vuoto → P → D → S → C</p>
    </div>
  );
}
